/**
 * FROZEN API contract for the unified entity audit-history endpoint.
 *
 * GET /audit-history/{subjectType}/{subjectId}/paginated
 * Query: page, perPage (default 25), eventTypes[], from, to, actorId
 */

export type EntityHistorySubjectType =
    | 'member'
    | 'unit'
    | 'transaction'
    | 'violation'
    | 'owner'
    | 'payment'
    | 'charge'
    | 'request'
    | 'document'
    | 'recurring_charge'
    | 'payable'
    | 'address'
    | 'organization'
    | 'organization_profile'
    | 'user'
    | 'payabli_payable'
    | 'payable_approval'
    | 'payment_attempt'
    | 'payment_item'
    | 'coa_category'
    | 'coa_fixed_asset'
    | 'coa_liability'
    | 'coa_equity';

/**
 * Canonical human-readable labels for each subject — mirrors
 * `App\Support\Audit\AuditSubjectTypes::humanLabels()` on the backend.
 * Keep in sync if subjects are added/removed.
 *
 * This is the single source of truth for subject labels in the UI.
 * Consumers that need a *marketing override* (e.g. dialog title shows
 * "Invoice" where the canonical label is "Charge") should layer
 * `SUBJECT_MARKETING_LABEL_OVERRIDES` on top, not maintain a parallel map.
 */
export const SUBJECT_CANONICAL_LABELS: Record<EntityHistorySubjectType, string> = {
    address: 'Address',
    charge: 'Charge',
    coa_category: 'Category',
    coa_equity: 'Equity',
    coa_fixed_asset: 'Asset',
    coa_liability: 'Liability',
    document: 'Document',
    member: 'Member',
    organization: 'Organization',
    organization_profile: 'Organization Profile',
    owner: 'Owner',
    payable: 'Payable',
    payable_approval: 'Payable Approval',
    payabli_payable: 'Payabli Payable',
    payment: 'Payment',
    payment_attempt: 'Payment Attempt',
    payment_item: 'Payment Item',
    recurring_charge: 'Recurring Charge',
    request: 'Request',
    transaction: 'Transaction',
    unit: 'Unit',
    user: 'User',
    violation: 'Violation',
};

/**
 * Marketing overrides applied on top of `SUBJECT_CANONICAL_LABELS` when
 * we surface the label to customers in user-facing copy (dialog titles,
 * banners, etc.) — but NOT in admin tooling like the lookup dropdown,
 * which should show the canonical class names so supers can find what
 * they're searching for.
 *
 * Today's only overrides: charges are sold as "Invoices" to property
 * managers; recurring charges as "Recurring Invoices".
 */
export const SUBJECT_MARKETING_LABEL_OVERRIDES: Partial<Record<EntityHistorySubjectType, string>> = {
    charge: 'Invoice',
    recurring_charge: 'Recurring Invoice',
};

/** Resolve the customer-facing display label (with marketing overrides). */
export function subjectDisplayLabel(subjectType: EntityHistorySubjectType): string
{
    return SUBJECT_MARKETING_LABEL_OVERRIDES[subjectType]
        ?? SUBJECT_CANONICAL_LABELS[subjectType]
        ?? 'Entity';
}

/**
 * Subject-key → canonical label tuples for the admin lookup dialog.
 * Alphabetized by label for predictable UX. Kept as a derived array
 * (no duplicated literal map) so the admin dropdown always matches
 * `SUBJECT_CANONICAL_LABELS`.
 */
export const ENTITY_HISTORY_SUBJECT_LABELS: Array<{ key: EntityHistorySubjectType; label: string }> =
    (Object.entries(SUBJECT_CANONICAL_LABELS) as Array<[EntityHistorySubjectType, string]>)
        .filter(([key]) => !key.startsWith('coa_'))
        .map(([key, label]) => ({ key, label }))
        .sort((a, b) => a.label.localeCompare(b.label));

/**
 * Audit row source. Backend currently only emits `'audit'` (Owen-IT).
 * `'activity'` is reserved for the future Spatie activity_log wiring
 * described in `core/app/Support/Audit/README.md`.
 */
export type EntityHistorySource = 'audit' | 'activity';

/**
 * Audit row actor. Reconciled with what `EntityHistoryController::formatActor()`
 * actually emits — see PR #2950 / #4196 review feedback.
 *
 * Notes:
 *  - `type` is always `'user'` today. The backend builds this from
 *    `entityKey($user_type) ?? 'user'`, and `user_type` is effectively always
 *    `App\Models\Entities\User` in production. No row emits `'system'` yet.
 *  - `impersonatedBy` is a bare user id (or null), not an object. The
 *    template only checks truthiness today.
 *  - `name` and `email` can both be null on system-triggered audits whose
 *    user has no first/last name (e.g. legacy seed accounts).
 */
export interface EntityHistoryActor
{
    type: 'user';
    id: number | null;
    name: string | null;
    email: string | null;
    impersonatedBy: number | null;
}

export interface EntityHistoryAuditable
{
    type: string;
    id: number;
    label?: string;
}

/**
 * The actual record this audit row is about. May differ from the dialog's
 * subject when child auditables are surfaced alongside their parent
 * (e.g. Charge audits surfacing under Unit history). Frontend shows this
 * in the "What" column so users can distinguish rows.
 */
export interface EntityHistoryRecord
{
    /**
     * Canonical camelCase short key for the underlying model.
     *
     * Single source of truth lives in core's
     * `App\Support\Audit\AuditSubjectTypes::entityKey()`. Examples:
     *   - whitelisted subjects: `unit`, `member`, `paymentItem`,
     *     `recurringCharge`, `payableApproval`
     *   - child auditables (not subjects, surfaced alongside their parent):
     *     `chargeLateFee`, `paymentAttemptFee`, `unitOccupancy`,
     *     `membership`, `note`, `violationStatementComment`
     *
     * Note this is NOT the same naming space as the URL slug `subjectType`
     * (which stays snake_case — `payment_item`, `recurring_charge`). The URL
     * slug is a path-segment convention; this is a data-value convention.
     */
    type: string;
    id: number;
    /** Optional human label (e.g. unit title, charge title). */
    label?: string | null;
    /** Optional FQCN for the model class (for debug / tooltips). */
    class?: string;
}

export interface EntityHistoryChange
{
    field: string;
    label: string;
    oldValue: unknown;
    newValue: unknown;
    /** Resolved label for `oldValue` when the field is a known FK (e.g. unit_id). */
    oldLabel?: string | null;
    /** Resolved label for `newValue` when the field is a known FK. */
    newLabel?: string | null;
    /** Click-through URL for `oldValue` when the FK has a detail page. */
    oldUrl?: string | null;
    /** Click-through URL for `newValue` when the FK has a detail page. */
    newUrl?: string | null;
    /**
     * Full attribute snapshot of the related record when the FK resolves to
     * one. Lets the UI offer an inline "drill into this record" expansion
     * showing every column on the related model. Honors model $hidden.
     */
    oldRelated?: Record<string, unknown> | null;
    newRelated?: Record<string, unknown> | null;
}

export interface EntityHistoryEntry
{
    id: string;
    source: EntityHistorySource;
    /**
     * Owen-IT audit tags exposed to the UI for conditional rendering
     * (e.g. `legacy_violation_history` rows render `content` as HTML
     * because the legacy data was stored with email-style markup).
     */
    tags?: string[];
    event: string;
    /**
     * ISO-8601 timestamp. Backend emits `optional($audit->created_at)->toIso8601String()`,
     * so this can be null for any audit row missing `created_at` (rare; mostly
     * a defensive null since most rows are auto-stamped by Owen-IT).
     */
    occurredAt: string | null;
    /**
     * Audit actor. Backend emits `null` whenever both `user_id` and the loaded
     * user model are null — common for system/console/webhook audits and for
     * synthetic baseline rows whose creator can't be inferred.
     */
    actor: EntityHistoryActor | null;
    auditable: EntityHistoryAuditable | null;
    entity?: EntityHistoryRecord | null;
    changes: EntityHistoryChange[] | null;
    message: string | null;
}

export interface EntityHistoryMeta
{
    currentPage: number;
    total: number;
    perPage: number;
    totalPages: number;
}

export interface EntityHistoryFilters
{
    events: string[];
    actors: { id: number; name: string }[];
}

export interface EntityHistoryResponse
{
    data: EntityHistoryEntry[];
    meta: EntityHistoryMeta;
    filters: EntityHistoryFilters;
}

export interface EntityHistoryQueryParams
{
    page?: number;
    perPage?: number;
    eventTypes?: string[];
    from?: string;
    to?: string;
    actorId?: number;
}
